// The capture window and the panel's tabs (60.11.580). The window tells every tab what it holds - on each change, when a
// tab says hello after a refresh, and every few seconds besides - and does what the tabs ask: stop the share, pause or
// stop the recording, and hand what was recorded to the tab whose reply box asks for it. A tab that hears nothing for a
// while lets the window's captures go (the window was closed without a word).
import {CaptureRequest, captureChannel, inCaptureWindow} from './capture-window'
import {
  dropScreenRecording,
  getScreenRecording,
  mirrorScreenRecording,
  pauseScreenRecording,
  receiveScreenRecording,
  resumeScreenRecording,
  ScreenRecordingCommand,
  ScreenRecordingState,
  stopScreenRecording,
  subscribeScreenRecording,
  takeScreenRecording,
} from './screen-recording'
import {
  getScreenShare,
  mirrorScreenShare,
  ScreenShareState,
  stopScreenShare,
  subscribeScreenShare,
} from './screen-share'
import {steadyTimeout} from './steady-timer'

export const HEARTBEAT_MS = 4000
export const SILENT_GONE_MS = 12_000

type Command = {action: 'stop-share'} | ScreenRecordingCommand

export type CaptureMessage =
  | {type: 'state'; share: ScreenShareState; recording: ScreenRecordingState}
  | {type: 'hello'}
  | {type: 'closing'}
  | {type: 'prepare'; request: CaptureRequest}
  | {type: 'command'; command: Command; from: string}
  | {type: 'recording'; to: string; ticketNumber: string; file: File; seconds: number}

/** The recording as the tabs see it: what was recorded stays in the window until a reply box asks for it. */
const told = ({recording, ...state}: ScreenRecordingState): ScreenRecordingState => state

/**
 * In the capture window: tells the tabs what it holds and does what they ask. `onPrepare` is a request from a tab for
 * another capture (the customer chooses its screen in the window). The answer stops it.
 */
export const hostCaptures = (onPrepare: (request: CaptureRequest) => void) => {
  const channel = captureChannel()
  if (!channel) return () => undefined
  const announce = () =>
    channel.postMessage({
      type: 'state',
      share: getScreenShare(),
      recording: told(getScreenRecording()),
    } as CaptureMessage)

  const obey = (command: Command, from: string) => {
    if (command.action === 'stop-share') stopScreenShare()
    else if (command.action === 'pause') pauseScreenRecording()
    else if (command.action === 'resume') resumeScreenRecording()
    else if (command.action === 'stop') stopScreenRecording()
    else if (command.action === 'cancel') stopScreenRecording(true)
    else if (command.action === 'drop') dropScreenRecording()
    else if (command.action === 'take' && command.ticketNumber) {
      const recording = takeScreenRecording(command.ticketNumber)
      if (!recording) return
      channel.postMessage({
        type: 'recording',
        to: from,
        ticketNumber: command.ticketNumber,
        file: recording.file,
        seconds: recording.seconds,
      } as CaptureMessage)
      URL.revokeObjectURL(recording.url)
    }
  }

  const heard = (event: MessageEvent<CaptureMessage>) => {
    const message = event.data
    if (message?.type === 'hello') announce()
    else if (message?.type === 'prepare') onPrepare(message.request)
    else if (message?.type === 'command') obey(message.command, message.from)
  }
  channel.addEventListener('message', heard)
  const offShare = subscribeScreenShare(announce)
  const offRecording = subscribeScreenRecording(announce)
  let cancelBeat = () => {}
  const beat = () => {
    announce()
    cancelBeat = steadyTimeout(beat, HEARTBEAT_MS)
  }
  beat()
  const goodbye = () => channel.postMessage({type: 'closing'} as CaptureMessage)
  window.addEventListener('pagehide', goodbye)
  return () => {
    channel.removeEventListener('message', heard)
    offShare()
    offRecording()
    cancelBeat()
    window.removeEventListener('pagehide', goodbye)
  }
}

let mirroring = false
const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

/** In a panel tab (once): shows and steers what the capture window holds; after a refresh it asks again at once. */
export const mirrorCaptures = () => {
  if (mirroring || inCaptureWindow()) return
  const channel = captureChannel()
  if (!channel) return
  mirroring = true
  const ask = (command: Command) =>
    channel.postMessage({type: 'command', command, from: tabId} as CaptureMessage)
  const forget = () => {
    mirrorScreenShare(null)
    mirrorScreenRecording(null)
  }
  let cancelSilence = () => {}
  channel.addEventListener('message', (event: MessageEvent<CaptureMessage>) => {
    const message = event.data
    if (message?.type === 'state') {
      mirrorScreenShare(message.share, () => ask({action: 'stop-share'}))
      mirrorScreenRecording(message.recording, ask)
      cancelSilence()
      cancelSilence = steadyTimeout(forget, SILENT_GONE_MS)
    } else if (message?.type === 'closing') {
      cancelSilence()
      forget()
    } else if (message?.type === 'recording' && message.to === tabId) {
      receiveScreenRecording(message.ticketNumber, message.file, message.seconds)
    }
  })
  channel.postMessage({type: 'hello'} as CaptureMessage)
}
